import { animate, style, transition, trigger } from '@angular/animations';

import { Component, effect, inject, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { FilledButtonComponent, OutlinedButtonComponent } from '@custom-components/general/button';
import { SpinnerComponent } from '@custom-components/general/spinner/spinner.component';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { SkeletonBlockComponent } from '@custom-components/general/skeleton/skeleton-block/skeleton-block.component';
import { PAGINATION_DEFAULT } from '@core/constants';
import { DeviceConfigFileDetailsModel, DeviceConfigFileVersion } from '@core/models/setting.model';
import { ShipmentDeviceDetails } from '@core/models/shipment.model';
import { TranslateModule } from '@ngx-translate/core';
import { CompanyStore } from '@root/app/store/company.store';
import { DeviceStore } from '@root/app/store/device.store';
import { SettingsStore } from '@root/app/store/settings.store';

@Component({
  selector: 'xemex-device-config-file-details-dialog',
  imports: [
    MatIconModule,
    MatButtonModule,
    FilledButtonComponent,
    OutlinedButtonComponent,
    MatTableModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    TranslateModule,
    SkeletonBlockComponent,
    SpinnerComponent,
  ],
  standalone: true,
  templateUrl: './device-config-file-details-dialog.component.html',
  styleUrl: './device-config-file-details-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  animations: [
    trigger('versionChange', [
      transition('* => *', [
        style({ opacity: 0 }),
        animate('100ms ease-in', style({ opacity: 1 })),
      ]),
    ]),
  ],
})
export class DeviceConfigFileDetailsDialogComponent {
  readonly dialogRef = inject(MatDialogRef<DeviceConfigFileDetailsDialogComponent>);
  deviceStore = inject(DeviceStore);
  companyStore = inject(CompanyStore);
  settingsStore = inject(SettingsStore);
  currentFileVersion: DeviceConfigFileVersion | undefined;
  configFileDetails: DeviceConfigFileDetailsModel | undefined;
  isLoadingConfigFileDetails = false;
  isLoadingConfigFileContent = false;
  isDownloadingFile = false;
  isDeletingFile = false;
  data = inject(MAT_DIALOG_DATA);
  dataSource = new MatTableDataSource<ShipmentDeviceDetails>([]);
  pageLength = 0;
  pageSize = PAGINATION_DEFAULT.limit;

  versionsColumns = ['versionId', 'isDeleted', 'description', 'createdOn', 'createdBy'];

  get dynamicParameterNames(): string {
    return this.currentFileVersion?.dynamicParameters?.map(p => p.name)?.join(', ') || '-';
  }

  /** Partner names resolved from the version's partner OIDs (never shows raw OIDs). */
  get partnerNames(): string {
    const cache = this.companyStore.partnerNameCache();
    const names = (this.currentFileVersion?.partnerOids ?? [])
      .map(oid => cache[oid])
      .filter(Boolean);
    return names.length ? names.join(', ') : '-';
  }

  constructor() {
    this.configFileDetails = this.data.fileDetail;
    if (this.data.fileId) {
      this.settingsStore.getDeviceConfigFileDetails(this.data.fileId);
    }
    effect(() => {
      this.configFileDetails = this.settingsStore.deviceConfigFileDetails();

      const targetVersionId = this.data.versionId;
      this.currentFileVersion =
        (targetVersionId
          ? this.configFileDetails?.versions.find(v => v.versionId === targetVersionId)
          : undefined) ?? this.configFileDetails?.versions.at(-1);
      // Resolve the version's partner OIDs to names for display.
      (this.currentFileVersion?.partnerOids ?? []).forEach(oid =>
        this.companyStore.getPartnerByIdMethod(oid),
      );
      this.isDownloadingFile = this.settingsStore.isDownloadingDeviceConfigFile();
      this.isLoadingConfigFileDetails = this.settingsStore.isLoadingDeviceConfigFileDetails();
    });
  }

  onDownload() {
    this.settingsStore.getDCFContentByVersionId(this.currentFileVersion!.versionId);
  }

  onVersionClicked(versionId: number | undefined) {
    if (!versionId) return;
    const version = this.configFileDetails?.versions.find(v => v.versionId === versionId);
    if (version) {
      this.currentFileVersion = version;
    }
  }

  onDelete() {
    this.settingsStore.deleteDeviceConfigFile(this.data.fileId);
  }
}
